<template>
  <div class="container" v-if="blog">
    <button class="back-link" @click="$emit('close-blog')">← Back to Blogs</button>
    
    <article class="blog-article">
      <h1 class="main-title">{{ blog.title }}</h1>
      
      <div class="meta-data">
        <span class="author">By {{ blog.author }}</span>
        <span class="date">{{ formatDate(blog.date) }}</span>
        <span class="stats">👁️ {{ views }} Views</span>
      </div>

      <div class="content ql-snow">
        <div ref="editor" class="ql-editor"></div>
      </div>

      <div class="engagement-section">
        <button 
          class="cta-button" 
          @click="handleLike" 
          :disabled="hasLiked"
          :class="{ 'liked': hasLiked }">
          {{ hasLiked ? 'Liked ❤️' : 'Like ♡' }}
        </button>
      </div>
    </article>
  </div>
</template>

<script>
import Quill from 'quill';
import 'quill/dist/quill.snow.css'; 

export default {
  name: "ViewBlog",
  props: {
    blogData: {
      type: Object,
      required: true
    }
  },
  emits: ['append-extra', 'close-blog'],
  data() {
    return {
      blog: null,
      views: 0,
      likes: 0,
      hasLiked: false,
      quillInstance: null,
      viewTimer: null
    }
  },
  created() {
    this.loadblog();
    this.checkAnonymousSession();
  },
  async mounted() {
    await this.$nextTick(); 
    this.renderQuillContent();
    this.viewTimer = setTimeout(() => {
      this.recordView();
    }, 2000);
  },
  beforeUnmount() {
    if (this.viewTimer) {
      clearTimeout(this.viewTimer);
      this.viewTimer = null;
    }
    if (this.quillInstance) {
      this.quillInstance = null;
    }
  },
  methods: {
    loadblog() {
      this.blog = this.blogData;
      this.views = this.blogData.views || 0;
      this.likes = this.blogData.likes || 0;
    },
    
    // Converts DB ISO strings to readable dates with time
    formatDate(dateString) {
        if (!dateString) return "Unknown Date";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString; 

        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }).format(date);
    },

    checkAnonymousSession() {
      const likedPosts = JSON.parse(localStorage.getItem('anonymous_likes') || '[]');
      if (likedPosts.includes(this.blog.id)) {
        this.hasLiked = true;
      }
    },

    renderQuillContent() {
      if (!this.$refs.editor || !this.blog?.content) return;

      this.quillInstance = new Quill(this.$refs.editor, {
        modules: { toolbar: false },
        readOnly: true,
        theme: 'snow'
      });

      try {
        const delta = typeof this.blog.content === 'string' 
          ? JSON.parse(this.blog.content) 
          : this.blog.content;
        this.quillInstance.setContents(delta);
      } catch (e) {
        console.error("Failed parsing Quill Delta dataset structure:", e);
        this.$refs.editor.innerHTML = this.blog.content;
      }
    },

    recordView() {
      const viewedPosts = JSON.parse(localStorage.getItem('anonymous_views') || '[]');
      
      if (!viewedPosts.includes(this.blog.id)) {
        this.views += 1;
        this.$emit('append-extra', { blogId: this.blog.id, type: 'views' });
        
        viewedPosts.push(this.blog.id);
        localStorage.setItem('anonymous_views', JSON.stringify(viewedPosts));
      }
    },

    handleLike() {
      if (!this.hasLiked) {
        this.hasLiked = true;
        this.likes += 1;
        
        this.$emit('append-extra', { blogId: this.blog.id, type: 'likes' }); 
        
        const likedPosts = JSON.parse(localStorage.getItem('anonymous_likes') || '[]');
        likedPosts.push(this.blog.id);
        localStorage.setItem('anonymous_likes', JSON.stringify(likedPosts));
      }
    }
  }
}
</script>

<style scoped>
:root {
    --color-maroon: #5D0E1F;    
    --color-gold: #FBBF24;      
    --color-earth: #A0522D;     
    --color-dark: #1c1917;      
    --font-serif: 'Playfair Display', serif; 
    --font-sans: 'Inter', sans-serif;       
}

.container {
    max-width: 800px;
    margin: 0 auto;
    /* Expanded upwards by removing the 3rem top padding */
    padding: 0.5rem 1.5rem 3rem 1.5rem; 
    font-family: var(--font-sans);
}

.back-link {
    background: none;
    border: none;
    color: var(--color-earth);
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 2rem;
    padding: 0;
    transition: color 0.3s ease;
}

.back-link:hover { color: var(--color-maroon); }

.blog-article {
    background-color: white;
    padding: 3rem;
    border-radius: 1rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
}

.main-title {
    font-family: var(--font-serif);
    font-size: 3rem;
    font-weight: 700;
    color: var(--color-maroon);
    margin-bottom: 1rem;
    line-height: 1.2;
}

.meta-data {
    display: flex;
    gap: 1.5rem;
    border-bottom: 1px solid #eaeaea;
    padding-bottom: 1.5rem;
    margin-bottom: 2rem;
    color: #666;
    font-size: 0.95rem;
}

.meta-data .author { font-weight: 600; color: var(--color-dark); }

/* Remove the default gray border Quill attaches to the container */
.content :deep(.ql-container.ql-snow) {
    border: none !important;
    font-family: inherit;
}

.content :deep(.ql-editor) {
    font-size: 1.15rem;
    color: #4a4a4a;
    line-height: 1.8;
    padding: 0; 
}

.content :deep(.ql-editor img) {
    max-width: 100%;
    height: auto;
    border-radius: 0.5rem;
    margin: 1.5rem 0;
}

.engagement-section {
    margin-top: 3rem;
    display: flex;
    justify-content: flex-end; /* Align the like button to the right */
}

/* Reduced size and adjusted formatting for the CTA button */
.cta-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: #5D0E1F; 
    color: white;
    padding: 0.5rem 1.5rem; /* Substantially reduced padding */
    border: none;
    border-radius: 30px; 
    font-weight: 600;
    font-size: 0.95rem; /* Reduced font size */
    cursor: pointer;
    transition: background-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15); 
}

.cta-button:hover:not(:disabled) {
    background-color: #4a0c19; 
    /* Fixed CSS syntax error here */
    transform: translateY(-2px) scale(1.02); 
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
}

.cta-button:disabled {
    background-color: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
}

.cta-button.liked {
    background-color: #fef2f2;
    color: #ef4444;
    border: 1px solid #fecaca;
}

@media (max-width: 768px) {
    .blog-article { padding: 1.5rem; }
    .main-title { font-size: 2.2rem; }
}
</style>